<template>
  <AuthShell>
    <v-form v-if="!passwordUpdated" class="auth-form" @submit.prevent="updatePassword">
      <h1 class="auth-form__title">Choose a new password</h1>
      <p class="auth-form__sub">At least 11 characters with an upper and lower case letter, a number and a symbol.</p>

      <div class="auth-form__field">
        <label for="new-password" class="auth-form__label">New password</label>
        <v-text-field
          id="new-password"
          v-model="newPassword"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="new-password"
          :error-messages="getPasswordErrorMessage"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showPassword = !showPassword" />
      </div>

      <div class="auth-form__meter" aria-hidden="true">
        <i v-for="step in 4" :key="step" :class="{ on: step <= passwordStrength }"></i>
      </div>

      <div class="auth-form__field">
        <label for="confirm-password" class="auth-form__label">Confirm password</label>
        <v-text-field
          id="confirm-password"
          v-model="passwordConfirmation"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="new-password"
          :error-messages="getPasswordMatchErrorMessage"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showPassword = !showPassword" />
      </div>

      <v-btn color="primary" block size="large" type="submit" :loading="showPreloader">Update password</v-btn>

      <v-alert v-if="errors.length > 0" type="error" variant="tonal">
        <div v-for="error in errors" :key="error">{{ error }}</div>
      </v-alert>
    </v-form>

    <div v-else class="auth-form">
      <h1 class="auth-form__title">Password changed</h1>
      <p class="auth-form__sub">You can sign in with your new password now.</p>
      <v-btn color="primary" block size="large" @click="redirectionToLoginPage">Continue to sign in</v-btn>
    </div>
  </AuthShell>
</template>

<script setup>
import { ref, computed } from "vue";
import authService from "@/services/authService";
import { useRoute, useRouter } from "vue-router";
import { tryParseApiErrors } from "@/utils";
import { FORM_VALIDATION_PATTERNS } from "@/utils/constants";
import AuthShell from "@/components/AuthShell.vue";

const errors = ref([]);
const showPreloader = ref(false);
const showPassword = ref(false);
const route = useRoute();
const router = useRouter();

const formButtonClicked = ref(false);
const passwordUpdated = ref(false);

const newPassword = ref("");
const passwordConfirmation = ref("");

// Validators
const isPasswordValid = computed(() => {
  const password = newPassword.value;
  return FORM_VALIDATION_PATTERNS.PASSWORD_PATTERN.test(password);
});

const isPasswordMatchValid = computed(() => {
  return newPassword.value === passwordConfirmation.value;
});

/** Four filled bars means the password satisfies every rule the backend enforces. */
const passwordStrength = computed(() => {
  const password = newPassword.value;
  if (!password) return 0;
  let score = 0;
  if (password.length >= 11) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
});

const getPasswordErrorMessage = computed(() => {
  if (!formButtonClicked.value) return "";
  if (!newPassword.value) return "Please enter a password";
  if (!isPasswordValid.value) {
    return "Please enter a valid password (at least 11 characters, at least 1 uppercase and 1 lowercase letter, 1 number, and 1 symbol)";
  }
  return "";
});

const getPasswordMatchErrorMessage = computed(() => {
  if (!formButtonClicked.value) return "";
  if (!passwordConfirmation.value) return "Please confirm your password";
  if (!isPasswordMatchValid.value) return "Passwords must match";
  return "";
});

const updatePassword = async () => {
  formButtonClicked.value = true;

  if (isPasswordValid.value && isPasswordMatchValid.value) {
    showPreloader.value = true;
    try {
      await authService.passwordReset(newPassword.value, route.params.token);
      passwordUpdated.value = true;
      errors.value = [];
    } catch (error) {
      errors.value = tryParseApiErrors(error);
    } finally {
      showPreloader.value = false;
    }
  }
};

const redirectionToLoginPage = () => {
  router.push({ name: "login" });
};
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_auth-form.scss";
</style>
